import { I18nextProvider } from "react-i18next"
import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from "react"

import { LanguageContext } from "./useLanguage"
import i18n, {
  APP_LANGUAGES,
  LANGUAGE_STORAGE_KEY,
  type AppLanguage,
  ensureAppLanguage,
} from "@/shared/lib/i18n"

export type LanguageContextValue = {
  language: AppLanguage
  setLanguage: (language: AppLanguage) => void
  toggleLanguage: () => void
  availableLanguages: typeof APP_LANGUAGES
}

const persistLanguage = (language: AppLanguage) => {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch (error) {
    console.warn("Unable to persist language preference to localStorage", error)
  }
}

export function LanguageProvider({ children }: PropsWithChildren) {
  const [language, setLanguageState] = useState<AppLanguage>(() => ensureAppLanguage(i18n.language))

  useEffect(() => {
    const handleLanguageChanged = (nextLanguage: string) => {
      const sanitized = ensureAppLanguage(nextLanguage)
      setLanguageState(sanitized)
      persistLanguage(sanitized)
    }

    i18n.on("languageChanged", handleLanguageChanged)

    return () => {
      i18n.off("languageChanged", handleLanguageChanged)
    }
  }, [])

  const setLanguage = useCallback((nextLanguage: AppLanguage) => {
    void i18n.changeLanguage(nextLanguage)
  }, [])

  const toggleLanguage = useCallback(() => {
    const currentIndex = APP_LANGUAGES.indexOf(language)
    const nextLanguage = APP_LANGUAGES[(currentIndex + 1) % APP_LANGUAGES.length]
    setLanguage(nextLanguage)
  }, [language, setLanguage])

  useEffect(() => {
    if (typeof document === "undefined") {
      return
    }

    document.documentElement.lang = language
  }, [language])

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      toggleLanguage,
      availableLanguages: APP_LANGUAGES,
    }),
    [language, setLanguage, toggleLanguage],
  )

  return (
    <LanguageContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LanguageContext.Provider>
  )
}
